import React, { useRef, SyntheticEvent } from 'react';
import {
    FooterDescription,
    FooterContainer,
    ButtonWrapper,
    ButtonSignIn,
    ButtonRegister,
    ButtonHomeValue,
    FooterContent,
    Title,
    CouchImage
} from './HomepageRegistrationFooter.styles';

import homepageTranslations from 'public/locales/en/homepage.json';
import { getTranslator } from '../../../shared/i18n/getTranslator';
import { AuthModalMode } from 'shared/components/AuthModal/AuthModal.types';
import { MAIN_SITE_LINK } from '../../../constants';
import { useIsLoggedIn } from 'shared/hooks/useIsLoggedIn';
import triggerTracking from '@blueprint/tracking';
import { useRouter } from 'next/router';

const t = getTranslator(homepageTranslations);

import couchImage from 'public/assets/homepage/Couch_illustration.svg';

const headingTitle = t('homepage_registration_footer.headline');
const description = t('homepage_registration_footer.description');
const register = t('homepage_registration_footer.registration_button');
const signIn = t('homepage_registration_footer.sign_in_button');

const homeValuesHeading = t('homepage_registration_footer.home_values_heading');
const seeMoreOnHomeValues = t(
    'homepage_registration_footer.see_more_on_home_values'
);
const homeValuesCopy = t('homepage_registration_footer.home_values_copy');

type Props = {
    openAuthModal: (
        triggerOrigin: React.RefObject<HTMLElement> | null,
        mode: AuthModalMode
    ) => void;
};

export const HomepageRegistrationFooter = ({ openAuthModal }: Props) => {
    const isLoggedIn = useIsLoggedIn();
    const registerTriggerOrigin = useRef(null);
    const signInTriggerOrigin = useRef(null);
    const router = useRouter();

    const registerTracking = () => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'homepage',
            eventAction: 'registration_footer',
            eventLabel: 'register'
        });
    };

    const signInTracking = () => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'homepage',
            eventAction: 'registration_footer',
            eventLabel: 'sign_in'
        });
    };

    const handleClickRegister = async () => {
        registerTracking();
        openAuthModal(registerTriggerOrigin, 'register');
    };

    const handleClickSignIn = () => {
        signInTracking();
        openAuthModal(signInTriggerOrigin, 'sign-in');
    };

    const handleClickHomeValues = () => {
        router.push(`${MAIN_SITE_LINK}/home-values`);
    };

    return (
        <>
            <FooterContainer>
                <FooterContent>
                    {!isLoggedIn ? (
                        <>
                            <Title size="5">{headingTitle}</Title>
                            <FooterDescription>{description}</FooterDescription>
                            <ButtonWrapper>
                                <ButtonRegister
                                    href="/register/"
                                    ref={registerTriggerOrigin}
                                    variant={'primaryInverted'}
                                    onClick={(event: SyntheticEvent) => {
                                        event.preventDefault();
                                        handleClickRegister();
                                    }}
                                    isFullWidth={false}
                                    data-testid="register-button"
                                >
                                    {register}
                                </ButtonRegister>
                                <ButtonSignIn
                                    href="/signin/"
                                    ref={signInTriggerOrigin}
                                    variant={'tertiaryInverted'}
                                    isFullWidth={false}
                                    data-testid="sign-in-button"
                                    onClick={(event: SyntheticEvent) => {
                                        event.preventDefault();
                                        handleClickSignIn();
                                    }}
                                >
                                    {signIn}
                                </ButtonSignIn>
                            </ButtonWrapper>
                        </>
                    ) : (
                        <>
                            <Title size="5">{homeValuesHeading}</Title>
                            <FooterDescription>
                                {homeValuesCopy}
                            </FooterDescription>
                            <ButtonWrapper>
                                <ButtonHomeValue
                                    href="/home-values"
                                    variant={'primaryInverted'}
                                    onClick={(event: SyntheticEvent) => {
                                        event.preventDefault();
                                        handleClickHomeValues();
                                    }}
                                    isFullWidth={false}
                                    data-testid="home-values-button"
                                >
                                    {seeMoreOnHomeValues}
                                </ButtonHomeValue>
                            </ButtonWrapper>
                        </>
                    )}
                </FooterContent>
                <CouchImage src={couchImage} alt=""></CouchImage>
            </FooterContainer>
        </>
    );
};
